namespace $ {

	export type $shm_projects_data_version = {
		id: string
		run_id?: string
		version: string
		date: string
		requirements?: string
		changes: string | readonly ( string | $shm_projects_data_change )[]
		stage: 'Пререлиз' | 'Релиз'
		artifacts?: readonly $shm_projects_data_artifact[]
		tests?: readonly $shm_projects_data_test[]
		available?: boolean
	}

	export type $shm_projects_data_change = {
		title: string
		description?: string
		screenshots?: readonly string[]
	}

	export type $shm_projects_data_artifact = {
		kind: 'screenshot' | 'video' | 'report'
		title: string
		path: string
	}

	export type $shm_projects_data_test = {
		scenario: string
		title: string
		run: string
		status: string
		manifest: string
	}

	export type $shm_projects_data_tests = {
		scenarios: readonly string[]
		artifacts?: readonly {
			scenario: string
			include?: readonly string[]
		}[]
	}

	export type $shm_projects_data_product = {
		description: string
		problem: string
		audience: string
		value_proposition: string
		scenarios: string
		success_criteria: string
		stage: 'idea' | 'prototype' | 'mvp' | 'growing' | 'support' | 'archived'
	}

	export type $shm_projects_data_record = {
		id: string
		title: string
		type?: 'complex' | 'client' | 'server'
		directory: string
		version_file?: string
		directory_pending?: boolean
		archived?: boolean
		parent_id?: string
		readme: string
		agents?: string
		repositories?: readonly string[]
		remote_repository?: $shm_projects_github_repository
		base_branch?: string
		preview: string
		preview_default?: boolean
		delivery?: {
			type: 'hosted'
			project: string
			application: string
		}
		product?: $shm_projects_data_product
		deployment?: {
			driver: 'docker'
			context: string
			image: string
			target: string
			targets?: {
				prerelease: string
				release: string
			}
			server: string
			bundle?: {
				runtime: 'static' | 'node'
				modules: readonly string[]
				exclude?: readonly string[]
				dockerfile?: string
				dockerignore?: string
				files: readonly {
					source: string
					target: string
				}[]
				preview?: {
					environment_from?: {
						compose_project: string
						service: string
						keys: readonly string[]
					}
					volumes?: readonly {
						name: string
						target: string
					}[]
				}
			}
			components?: readonly $shm_projects_release_component[]
		}
		version: string
		version_date: string
		channels?: {
			prerelease?: string
			release?: string
		}
		tests?: $shm_projects_data_tests
		history: readonly $shm_projects_data_version[]
	}

	export class $shm_projects_data extends $mol_object2 {

		@ $mol_mem
		static records( next?: readonly $shm_projects_data_record[] ): readonly $shm_projects_data_record[] {
			if( next ) return next
			const url = new URL( this.$.$shm_projects_config.projects_api_url() )
			url.searchParams.set( 'projects', '1' )
			const token = this.$.$shm_projects_config.agent_token()
			const records = this.$.$mol_fetch.json(
				url.toString(),
				{
					cache: 'no-store',
					headers: token ? { 'X-Hitalama-Agent-Token': token } : {},
				},
			) as readonly $shm_projects_data_record[]
			const ids = new Set< string >()

			for( const record of records ) {
				if( !record.id || !record.title ) throw new Error( 'Project id and title are required' )
				if( ids.has( record.id ) ) throw new Error( `Duplicate project id: ${ record.id }` )
				ids.add( record.id )
			}

			return records
		}

		static record( id: string ) {
			return this.records().find( record => record.id === id ) ?? null
		}

		static delivery( record: $shm_projects_data_record ) {
			return $shm_projects_delivery.project( this.records(), record )
		}

		static preview( record: $shm_projects_data_record ) {
			return $shm_projects_delivery.preview( this.records(), record )
		}

		static state_key( id: string, field: string ) {
			return `shm_projects:${ id }:${ field }`
		}

		static session_ids( id: string, next?: readonly string[] ) {
			return this.$.$mol_state_local.value< readonly string[] >( this.state_key( id, 'opencode_sessions' ), next ) ?? []
		}

		static session_current( id: string, next?: string ) {
			return this.$.$mol_state_local.value< string >( this.state_key( id, 'opencode_session_current' ), next ) ?? ''
		}

		static version( id: string, next?: string ) {
			if( next !== undefined ) this.$.$mol_state_local.value< string >( this.state_key( id, 'version' ), next )
			return this.record( id )?.version ?? next ?? ''
		}

		static version_date( id: string, next?: string ) {
			if( next !== undefined ) this.$.$mol_state_local.value< string >( this.state_key( id, 'version_date' ), next )
			const date = this.record( id )?.version_date ?? next ?? ''
			return date === '-' ? '' : date
		}

		static version_changes( id: string, next?: string ) {
			return this.$.$mol_state_local.value< string >( this.state_key( id, 'version_changes' ), next ) ?? ''
		}

		static requirements( id: string, next?: string ) {
			return this.$.$mol_state_local.value< string >( this.state_key( id, 'requirements' ), next ) ?? ''
		}

		static requirements_version( id: string, next?: string ) {
			return this.$.$mol_state_local.value< string >( this.state_key( id, 'requirements_version' ), next ) ?? ''
		}

		static version_history( id: string, next?: readonly $shm_projects_data_version[] ) {
			const key = this.state_key( id, 'version_history' )
			if( next !== undefined ) return this.$.$mol_state_local.value< readonly $shm_projects_data_version[] >( key, next ) ?? next
			const stored = this.$.$mol_state_local.value< readonly $shm_projects_data_version[] >( key ) ?? []
			const seeded = this.record( id )?.history ?? []
			if( !stored.length ) return seeded
			const versions = new Set( seeded.map( record => `${ record.stage }:${ record.version }` ) )
			return [ ...seeded, ...stored.filter( record => !versions.has( `${ record.stage }:${ record.version }` ) ) ]
		}

	}

}
